import type {
  ContractRecord,
  ContractRecordMap
} from '@libs/data-access/entities'
import {
  isContractInProgressAt,
  isContractInProgressBetween
} from '@libs/data-access/queries'
import type { ReduxState } from '@spa/redux/ReduxState'
import { formatToIsoDay } from '@spa/utils/date'
import Immutable from 'immutable'
import moment from 'moment'

import {
  getDataByForeignId,
  getDataByForeignIds
} from '../../services/immutableUtils'
import { getCurrentAccount } from '../accounts/selectors'
import { getContractsByPlannableMembership } from '../memberships/selectors'
import { getWeeklyScheduleById } from '../weeklyschedules/selectors'

import { findAndMergeAmendmentsRelativeToDate } from './index'

export const getIsContractsLoading = state =>
  state.contracts.getIn(['meta', 'loading'])

export const getContractById = (state, contractId) =>
  state.contracts.getIn(['data', Number(contractId)]) as ContractRecord

export const getContractsByIds = (state, contractIds) =>
  contractIds?.map(id => state.contracts.getIn(['data', Number(id)]))

export const getContractsByMembershipId = (state, membershipId) =>
  getDataByForeignId(state, 'contracts', 'membership_id', membershipId)

export const getContractsByMembershipIds = (state, membershipIds) =>
  getDataByForeignIds(state, 'contracts', 'membership_id', membershipIds)

export const getDashboardContractsPerPage = (state, currentPage) =>
  state.contracts.getIn(['pagination', 'pages', currentPage], Immutable.Set())

export const getContractsByLocationId = (
  state,
  locationId: number
): ContractRecordMap =>
  getDataByForeignId(state, 'contracts', 'location_id', Number(locationId))

export const getPlannableContractsByLocationId = (
  state,
  locationId: number
) => {
  const contracts = getContractsByLocationId(state, locationId)
  return getContractsByPlannableMembership(state, contracts)
}

export const getContractsByLocationIds = (state, locationIds) =>
  getDataByForeignIds(state, 'contracts', 'location_id', locationIds)

export const getContracts = (state: ReduxState) => state.contracts.get('data')

export const getContractsByTeamId = (
  state,
  teamId: number
): ContractRecordMap =>
  getDataByForeignId(state, 'contracts', 'team_id', Number(teamId))

export const getPlannableContractsByTeamId = (state, teamId: number) => {
  const contracts = getContractsByTeamId(state, teamId)
  return getContractsByPlannableMembership(state, contracts)
}

export const getContractsByTeamIds = (state, teamIds) =>
  getDataByForeignIds(state, 'contracts', 'team_id', teamIds)

export const getContractsByAccessibleTeamId = (state, teamId) => {
  return state.contracts.get('data').filter(c => {
    return c.team_ids.includes(teamId)
  })
}

export const getContractsByAccessibleTeamIds = (state, teamIds) => {
  return state.contracts.get('data').filter(c => {
    return c.team_ids.map(id => teamIds.includes(id))
  })
}

export const getDashboardCustomPaginationContracts = (
  state,
  customPaginationKey,
  currentPage
) =>
  state.contracts.getIn(
    ['customPagination', customPaginationKey, 'pages', currentPage],
    Immutable.Set()
  )

/**
 * Get the most_relevant_contract given a membership ID
 */
export const getMostRelevantContractForMembershipId = (
  state: ReduxState,
  membershipId: number,
  date: string
) => {
  const contracts = getContractsByMembershipId(state, membershipId)

  if (!contracts || !contracts.size) {
    return
  }

  if (contracts.size === 1) {
    return contracts.first()
  }

  const currentContract = contracts.find(contract =>
    isContractInProgressAt(contract, date)
  )

  if (currentContract) {
    return currentContract
  }

  const sortedContracts = contracts
    .filter(contract => contract.contract_start)
    .sortBy(contract => contract.contract_start)

  return sortedContracts.last()
}

/**
 * Get all contracts related to a specific weeklyscheduleId
 * First the weeklyschedule is retrieved with its ID
 * Then the weeklyschedule.week is compared with the contract start/end
 */
export const getContractsInWeeklyschedule = (
  state: ReduxState,
  { weeklyscheduleId }: { weeklyscheduleId: number }
) => {
  const weeklyschedule = getWeeklyScheduleById(state, weeklyscheduleId)

  if (!weeklyschedule) {
    return Immutable.Map()
  }

  const account = getCurrentAccount(state)
  const contracts = state.contracts.get('data')
  const mergeAmendments = findAndMergeAmendmentsRelativeToDate(
    state,
    account,
    formatToIsoDay(moment(weeklyschedule.week).startOf('isoWeek'))
  )

  return contracts
    .filter(contract => {
      // Check if contract belongs to the weeklyschedule provided
      const isInTeam = contract.team_ids.includes(weeklyschedule.team_id)

      // Check if the contract dates belongs to the weeklyschedule week
      const contractIsInProgress = isContractInProgressBetween(
        contract,
        formatToIsoDay(moment(weeklyschedule.week).startOf('isoWeek')),
        formatToIsoDay(moment(weeklyschedule.week).endOf('isoWeek'))
      )
      return isInTeam && contractIsInProgress
    })
    .map(mergeAmendments)
}

export const getContractsPagination = (
  state: ReduxState
): {
  current_page: number
  pages: { [key: number]: number[] }
  total_pages: number
  total_count: number
  per_page: number
} => state.contracts.get('pagination').toJS()

export const getCurrentPageContracts = (state: ReduxState) => {
  const contractsPagination = getContractsPagination(state)
  const currentPage = contractsPagination?.current_page || 1

  return getContracts(state)
    ?.valueSeq()
    .filter(contract =>
      contractsPagination.pages[currentPage]?.includes(contract?.id)
    )
}
